import * as THREE from 'three'
import { GSplatScanLUTWriter } from '@/plugins/gsplat/effects/boxScanLut'
import {
  createColor,
  createVector3,
  type GSplatColorInput,
  type GSplatVector3Input,
  solveWaveTime,
  writeColorArray,
  writeVector3Array,
} from '@/plugins/gsplat/effects/effectUtils'
import {
  GSplatShaderEffect,
  type GSplatShaderEffectOptions,
} from '@/plugins/gsplat/effects/GSplatShaderEffect'

const MODE_SCAN = 0
const MODE_DOUBLE_WAVE_REVEAL = 1
const DEFAULT_MIN_PIXEL_SIZE = 0.5

const shaderGLSL = /* glsl */ `
uniform int uObbMode;
uniform bool uObbBypass;
uniform sampler2D uLUT;
uniform vec3 uObbOrigin;
uniform vec3 uObbAxisX;
uniform vec3 uObbAxisY;
uniform vec3 uObbAxisZ;
uniform vec3 uObbMin;
uniform vec3 uObbMax;
uniform vec3 uObbDirection;
uniform float uSweepStart;
uniform float uSweepEnd;
uniform float uTime;
uniform float uSpeed;
uniform float uAcceleration;
uniform float uDelay;
uniform vec3 uDotTint;
uniform vec3 uWaveTint;
uniform float uOscillationIntensity;
uniform float uBandWidth;
uniform float uDotSize;
uniform float uMaxRoundSize;
uniform float uRevealBlendDistance;

bool g_insideOBB;
vec4 g_lutValue;
float g_scanDistance;
float g_scanLength;
float g_dotWavePos;
float g_liftTime;
float g_liftWavePos;

float hash(vec3 p) {
  return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

vec3 computeObbLocal(vec3 center) {
  vec3 delta = center - uObbOrigin;
  return vec3(dot(delta, uObbAxisX), dot(delta, uObbAxisY), dot(delta, uObbAxisZ));
}

void initObbShared(vec3 center) {
  vec3 localCenter = computeObbLocal(center);
  g_insideOBB = all(greaterThanEqual(localCenter, uObbMin)) && all(lessThanEqual(localCenter, uObbMax));
  if (!g_insideOBB) {
    return;
  }

  float dirLen = length(uObbDirection);
  if (dirLen < 0.001) {
    g_insideOBB = false;
    return;
  }

  vec3 dir = uObbDirection / dirLen;
  float projection = dot(localCenter, dir);
  float sweepStart = min(uSweepStart, uSweepEnd);
  float sweepEnd = max(uSweepStart, uSweepEnd);
  g_scanLength = max(sweepEnd - sweepStart, 0.0001);
  g_scanDistance = projection - sweepStart;

  if (uObbMode == 0) {
    float t = clamp(g_scanDistance / g_scanLength, 0.0, 1.0);
    int texelX = int(t * 255.0);
    g_lutValue = texelFetch(uLUT, ivec2(texelX, 0), 0);
  } else {
    g_dotWavePos = uSpeed * uTime + 0.5 * uAcceleration * uTime * uTime;
    g_liftTime = max(0.0, uTime - uDelay);
    g_liftWavePos = uSpeed * g_liftTime + 0.5 * uAcceleration * g_liftTime * g_liftTime;
  }
}

void modifySplatCenter(inout vec3 center) {
  if (uObbBypass) {
    return;
  }
  initObbShared(center);
  if (!g_insideOBB || uObbMode == 0) {
    return;
  }

  if (g_scanDistance < 0.0 || g_scanDistance > g_scanLength) {
    return;
  }

  float liftOffset = 0.0;
  bool wavesActive = g_liftTime <= 0.0 || g_scanDistance > g_liftWavePos - 1.5 * uBandWidth;
  if (wavesActive) {
    float phase = hash(center) * 6.28318;
    liftOffset += sin(uTime * 3.0 + phase) * uOscillationIntensity * 0.25;
  }

  float distToLiftWave = abs(g_scanDistance - g_liftWavePos);
  if (distToLiftWave < uBandWidth && g_liftTime > 0.0) {
    float normalizedDist = distToLiftWave / uBandWidth;
    float liftAmount = (1.0 - normalizedDist) * sin(normalizedDist * 3.14159);
    liftOffset += liftAmount * uOscillationIntensity * 0.9;
  }

  if (abs(liftOffset) > 1e-6) {
    vec3 liftAxis = length(uObbAxisZ) > 0.001 ? normalize(uObbAxisZ) : vec3(0.0, 0.0, 1.0);
    center += liftAxis * liftOffset;
  }
}

void modifySplatCovariance(vec3 originalCenter, vec3 modifiedCenter, inout vec3 covA, inout vec3 covB) {
  if (uObbBypass) {
    return;
  }
  if (!g_insideOBB) {
    return;
  }

  if (uObbMode == 0) {
    float lutScale = g_lutValue.a;
    if (lutScale < 0.01) {
      gsplatMakeRound(covA, covB, 0.0);
      return;
    }

    if (lutScale < 1.0) {
      gsplatApplyUniformScale(covA, covB, lutScale);
    }
    return;
  }

  if (g_scanDistance < 0.0 || g_scanDistance > g_scanLength) {
    gsplatMakeRound(covA, covB, 0.0);
    return;
  }

  float scaleFactor;
  bool isLiftWave = g_liftTime > 0.0 && g_liftWavePos > g_scanDistance;

  if (isLiftWave) {
    scaleFactor = (g_liftWavePos >= g_scanDistance + uRevealBlendDistance)
      ? 1.0
      : mix(0.1, 1.0, (g_liftWavePos - g_scanDistance) / max(uRevealBlendDistance, 1e-5));
  } else if (g_scanDistance > g_dotWavePos + uRevealBlendDistance) {
    gsplatMakeRound(covA, covB, 0.0);
    return;
  } else if (g_scanDistance > g_dotWavePos - uRevealBlendDistance) {
    float distToWave = abs(g_scanDistance - g_dotWavePos);
    float normalizedWave = distToWave / max(uRevealBlendDistance, 1e-5);
    scaleFactor = (normalizedWave < 0.5)
      ? mix(0.1, 0.2, 1.0 - normalizedWave * 2.0)
      : mix(0.0, 0.1, smoothstep(g_dotWavePos + uRevealBlendDistance, g_dotWavePos - uRevealBlendDistance, g_scanDistance));
  } else {
    scaleFactor = 0.1;
  }

  if (scaleFactor >= 1.0) {
    return;
  }

  float origSize = gsplatExtractSize(covA, covB);

  if (isLiftWave) {
    float t = (scaleFactor - 0.1) * 1.111111;
    float dotSize = min(uDotSize * scaleFactor, uMaxRoundSize);
    float finalSize = min(mix(dotSize, origSize, t), origSize);
    vec3 roundA = covA;
    vec3 roundB = covB;
    vec3 scaledA = covA;
    vec3 scaledB = covB;
    gsplatMakeRound(roundA, roundB, finalSize);
    gsplatApplyUniformScale(scaledA, scaledB, scaleFactor);
    covA = mix(roundA, scaledA, t);
    covB = mix(roundB, scaledB, t);
  } else {
    float targetSize = min(min(scaleFactor * uDotSize, origSize), uMaxRoundSize);
    gsplatMakeRound(covA, covB, targetSize);
  }
}

void modifySplatColor(vec3 center, inout vec4 color) {
  if (uObbBypass) {
    return;
  }
  if (!g_insideOBB) {
    return;
  }

  if (uObbMode == 0) {
    color.rgb *= g_lutValue.rgb;
    return;
  }

  if (g_scanDistance < 0.0 || g_scanDistance > g_scanLength) {
    return;
  }

  if (g_liftTime > 0.0 && g_scanDistance >= g_liftWavePos - 1.5 * uBandWidth && g_scanDistance <= g_liftWavePos + 0.5 * uBandWidth) {
    float distToLift = abs(g_scanDistance - g_liftWavePos);
    float liftIntensity = smoothstep(1.5 * uBandWidth, 0.0, distToLift);
    color.rgb += uWaveTint * liftIntensity;
  } else if (g_scanDistance <= g_dotWavePos && (g_liftTime <= 0.0 || g_scanDistance > g_liftWavePos + 0.5 * uBandWidth)) {
    float distToDot = abs(g_scanDistance - g_dotWavePos);
    float dotIntensity = smoothstep(uBandWidth, 0.0, distToDot);
    color.rgb += uDotTint * dotIntensity;
  }
}
`

const shaderWGSL = /* wgsl */ `
var<private> g_insideOBB: bool;
var<private> g_lutValue: vec4f;
var<private> g_scanDistance: f32;
var<private> g_scanLength: f32;
var<private> g_dotWavePos: f32;
var<private> g_liftTime: f32;
var<private> g_liftWavePos: f32;

fn hash(value: vec3f) -> f32 {
  return fract(sin(dot(value, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

fn computeObbLocal(center: vec3f) -> vec3f {
  let delta = center - uniform.uObbOrigin;
  return vec3f(
    dot(delta, uniform.uObbAxisX),
    dot(delta, uniform.uObbAxisY),
    dot(delta, uniform.uObbAxisZ)
  );
}

fn initObbShared(center: vec3f) {
  let localCenter = computeObbLocal(center);
  g_insideOBB = all(localCenter >= uniform.uObbMin) &&
    all(localCenter <= uniform.uObbMax);
  if (!g_insideOBB) { return; }

  let directionLength = length(uniform.uObbDirection);
  if (directionLength < 0.001) {
    g_insideOBB = false;
    return;
  }

  let direction = uniform.uObbDirection / directionLength;
  let projection = dot(localCenter, direction);
  let sweepStart = min(uniform.uSweepStart, uniform.uSweepEnd);
  let sweepEnd = max(uniform.uSweepStart, uniform.uSweepEnd);
  g_scanLength = max(sweepEnd - sweepStart, 0.0001);
  g_scanDistance = projection - sweepStart;

  if (uniform.uObbMode == 0) {
    let t = clamp(g_scanDistance / g_scanLength, 0.0, 1.0);
    let texelX = i32(t * 255.0);
    g_lutValue = textureLoad(uLUT, vec2i(texelX, 0), 0);
  } else {
    g_dotWavePos = uniform.uSpeed * uniform.uTime +
      0.5 * uniform.uAcceleration * uniform.uTime * uniform.uTime;
    g_liftTime = max(0.0, uniform.uTime - uniform.uDelay);
    g_liftWavePos = uniform.uSpeed * g_liftTime +
      0.5 * uniform.uAcceleration * g_liftTime * g_liftTime;
  }
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  if (uniform.uObbBypass) { return; }
  initObbShared(*center);
  if (!g_insideOBB || uniform.uObbMode == 0) { return; }
  if (g_scanDistance < 0.0 || g_scanDistance > g_scanLength) { return; }

  var liftOffset = 0.0;
  let wavesActive = g_liftTime <= 0.0 ||
    g_scanDistance > g_liftWavePos - 1.5 * uniform.uBandWidth;
  if (wavesActive) {
    let phase = hash(*center) * 6.28318;
    liftOffset = liftOffset +
      sin(uniform.uTime * 3.0 + phase) * uniform.uOscillationIntensity * 0.25;
  }

  let distToLiftWave = abs(g_scanDistance - g_liftWavePos);
  if (distToLiftWave < uniform.uBandWidth && g_liftTime > 0.0) {
    let normalizedDist = distToLiftWave / uniform.uBandWidth;
    let liftAmount = (1.0 - normalizedDist) * sin(normalizedDist * 3.14159);
    liftOffset = liftOffset + liftAmount * uniform.uOscillationIntensity * 0.9;
  }

  if (abs(liftOffset) > 0.000001) {
    let liftAxis = select(
      vec3f(0.0, 0.0, 1.0),
      normalize(uniform.uObbAxisZ),
      length(uniform.uObbAxisZ) > 0.001
    );
    *center = *center + liftAxis * liftOffset;
  }
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  if (uniform.uObbBypass || !g_insideOBB) { return; }

  if (uniform.uObbMode == 0) {
    let lutScale = g_lutValue.a;
    if (lutScale < 0.01) {
      *scale = vec3f(0.0);
      return;
    }
    if (lutScale < 1.0) {
      *scale = (*scale) * lutScale;
    }
    return;
  }

  if (g_scanDistance < 0.0 || g_scanDistance > g_scanLength) {
    *scale = vec3f(0.0);
    return;
  }

  let originalSize = length(*scale);
  let isLiftWave = g_liftTime > 0.0 && g_liftWavePos > g_scanDistance;
  var scaleFactor = 0.1;
  if (isLiftWave) {
    scaleFactor = select(
      mix(0.1, 1.0, (g_liftWavePos - g_scanDistance) /
        max(uniform.uRevealBlendDistance, 0.00001)),
      1.0,
      g_liftWavePos >= g_scanDistance + uniform.uRevealBlendDistance
    );
  } else if (g_scanDistance > g_dotWavePos + uniform.uRevealBlendDistance) {
    *scale = vec3f(0.0);
    return;
  } else if (g_scanDistance > g_dotWavePos - uniform.uRevealBlendDistance) {
    let distToWave = abs(g_scanDistance - g_dotWavePos);
    let normalizedWave = distToWave /
      max(uniform.uRevealBlendDistance, 0.00001);
    scaleFactor = select(
      mix(0.0, 0.1, smoothstep(
        g_dotWavePos + uniform.uRevealBlendDistance,
        g_dotWavePos - uniform.uRevealBlendDistance,
        g_scanDistance
      )),
      mix(0.1, 0.2, 1.0 - normalizedWave * 2.0),
      normalizedWave < 0.5
    );
  }

  if (scaleFactor >= 1.0) { return; }
  if (isLiftWave) {
    let t = (scaleFactor - 0.1) * 1.111111;
    let dotSize = min(uniform.uDotSize * scaleFactor, uniform.uMaxRoundSize);
    let finalSize = min(mix(dotSize, originalSize, t), originalSize);
    *scale = mix(vec3f(finalSize), (*scale) * scaleFactor, t);
  } else {
    let targetSize = min(
      min(scaleFactor * uniform.uDotSize, originalSize),
      uniform.uMaxRoundSize
    );
    *scale = vec3f(targetSize);
  }
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  if (uniform.uObbBypass || !g_insideOBB) { return; }

  if (uniform.uObbMode == 0) {
    (*color) = vec4f((*color).rgb * g_lutValue.rgb, (*color).a);
    return;
  }

  if (g_scanDistance < 0.0 || g_scanDistance > g_scanLength) { return; }

  if (g_liftTime > 0.0 &&
      g_scanDistance >= g_liftWavePos - 1.5 * uniform.uBandWidth &&
      g_scanDistance <= g_liftWavePos + 0.5 * uniform.uBandWidth) {
    let distToLift = abs(g_scanDistance - g_liftWavePos);
    let liftIntensity = smoothstep(1.5 * uniform.uBandWidth, 0.0, distToLift);
    (*color) = vec4f((*color).rgb + uniform.uWaveTint * liftIntensity, (*color).a);
  } else if (g_scanDistance <= g_dotWavePos &&
             (g_liftTime <= 0.0 ||
              g_scanDistance > g_liftWavePos + 0.5 * uniform.uBandWidth)) {
    let distToDot = abs(g_scanDistance - g_dotWavePos);
    let dotIntensity = smoothstep(uniform.uBandWidth, 0.0, distToDot);
    (*color) = vec4f((*color).rgb + uniform.uDotTint * dotIntensity, (*color).a);
  }
}
`

export type GSplatObbShaderEffectMode = 'scan' | 'double-wave-reveal'

export interface GSplatObbProjectionRange {
  start: number
  end: number
  length: number
}

export interface GSplatObbShaderEffectOptions extends GSplatShaderEffectOptions {
  mode?: GSplatObbShaderEffectMode
  origin?: GSplatVector3Input
  axisX?: GSplatVector3Input
  axisY?: GSplatVector3Input
  axisZ?: GSplatVector3Input
  min?: GSplatVector3Input
  max?: GSplatVector3Input
  direction?: GSplatVector3Input
  sweepStart?: number
  sweepEnd?: number
  duration?: number
  visibleStart?: boolean
  visibleEnd?: boolean
  interval?: number
  invertTint?: boolean
  baseTint?: GSplatColorInput
  edgeTint?: GSplatColorInput
  tint?: GSplatColorInput
  speed?: number
  acceleration?: number
  delay?: number
  dotTint?: GSplatColorInput
  waveTint?: GSplatColorInput
  oscillationIntensity?: number
  bandWidth?: number
  dotSize?: number
  maxRoundSize?: number
  revealBlendDistance?: number
  minPixelSize?: number
}

export class GSplatObbShaderEffect extends GSplatShaderEffect {
  mode: GSplatObbShaderEffectMode
  origin: THREE.Vector3
  axisX: THREE.Vector3
  axisY: THREE.Vector3
  axisZ: THREE.Vector3
  min: THREE.Vector3
  max: THREE.Vector3
  direction: THREE.Vector3
  sweepStart: number | null
  sweepEnd: number | null
  duration: number
  visibleStart: boolean
  visibleEnd: boolean
  interval: number
  invertTint: boolean
  baseTint: THREE.Color
  edgeTint: THREE.Color
  tint: THREE.Color
  speed: number
  acceleration: number
  delay: number
  dotTint: THREE.Color
  waveTint: THREE.Color
  oscillationIntensity: number
  bandWidth: number
  dotSize: number
  maxRoundSize: number
  revealBlendDistance: number
  minPixelSize: number

  private readonly _originArray = [0, 0, 0]
  private readonly _axisXArray = [0, 0, 0]
  private readonly _axisYArray = [0, 0, 0]
  private readonly _axisZArray = [0, 0, 0]
  private readonly _minArray = [0, 0, 0]
  private readonly _maxArray = [0, 0, 0]
  private readonly _directionArray = [0, 0, 0]
  private readonly _dotTintArray = [0, 0, 0]
  private readonly _waveTintArray = [0, 0, 0]
  private readonly _normalizedDirection = new THREE.Vector3()
  private readonly _lutData = new Uint16Array(256 * 4)
  private readonly _lutWriter = new GSplatScanLUTWriter()
  private _lutTexture: THREE.DataTexture | null = null

  constructor(options: GSplatObbShaderEffectOptions = {}) {
    super(options)
    this.mode = options.mode ?? 'scan'
    this.origin = createVector3(options.origin, new THREE.Vector3(0, 0, 0))
    this.axisX = createVector3(options.axisX, new THREE.Vector3(1, 0, 0))
    this.axisY = createVector3(options.axisY, new THREE.Vector3(0, 1, 0))
    this.axisZ = createVector3(options.axisZ, new THREE.Vector3(0, 0, 1))
    this.min = createVector3(options.min, new THREE.Vector3(-0.5, -0.5, -0.5))
    this.max = createVector3(options.max, new THREE.Vector3(0.5, 0.5, 0.5))
    this.direction = createVector3(options.direction, new THREE.Vector3(0, 1, 0))
    this.sweepStart = Number.isFinite(options.sweepStart) ? (options.sweepStart as number) : null
    this.sweepEnd = Number.isFinite(options.sweepEnd) ? (options.sweepEnd as number) : null
    this.duration = options.duration ?? 1
    this.visibleStart = options.visibleStart ?? false
    this.visibleEnd = options.visibleEnd ?? true
    this.interval = options.interval ?? 0.2
    this.invertTint = options.invertTint ?? false
    this.baseTint = createColor(options.baseTint, new THREE.Color(1, 1, 1))
    this.edgeTint = createColor(options.edgeTint, new THREE.Color(1, 0, 1))
    this.tint = createColor(options.tint, new THREE.Color(1, 1, 1))
    this.speed = options.speed ?? 5
    this.acceleration = options.acceleration ?? 0
    this.delay = options.delay ?? 3
    this.dotTint = createColor(options.dotTint, new THREE.Color(0, 1, 1))
    this.waveTint = createColor(options.waveTint, new THREE.Color(1, 0.5, 0))
    this.oscillationIntensity = options.oscillationIntensity ?? 0.2
    this.bandWidth = options.bandWidth ?? 1
    this.dotSize = options.dotSize ?? 0.05
    this.maxRoundSize = options.maxRoundSize ?? this.dotSize
    this.revealBlendDistance = options.revealBlendDistance ?? 1
    this.minPixelSize = options.minPixelSize ?? DEFAULT_MIN_PIXEL_SIZE
    this.uniforms.uObbBypass = { value: false }
    this.uniforms.minPixelSize = { value: this.minPixelSize }
    this._lutTexture = this.createLUTTexture()
  }

  getShaderGLSL(): string {
    return shaderGLSL
  }

  getShaderWGSL(): string {
    return shaderWGSL
  }

  get lutTexture(): THREE.DataTexture | null {
    return this._lutTexture
  }

  getProjectionRange(): GSplatObbProjectionRange {
    const dirLen = this.direction.length()
    if (dirLen < 0.001) {
      return { start: 0, end: 0, length: 0 }
    }

    this._normalizedDirection.copy(this.direction).normalize()
    const dir = this._normalizedDirection
    const start =
      dir.x * (dir.x >= 0 ? this.min.x : this.max.x) +
      dir.y * (dir.y >= 0 ? this.min.y : this.max.y) +
      dir.z * (dir.z >= 0 ? this.min.z : this.max.z)
    const end =
      dir.x * (dir.x >= 0 ? this.max.x : this.min.x) +
      dir.y * (dir.y >= 0 ? this.max.y : this.min.y) +
      dir.z * (dir.z >= 0 ? this.max.z : this.min.z)

    return { start, end, length: Math.max(0, end - start) }
  }

  getSweepProjectionRange(): GSplatObbProjectionRange {
    const supportRange = this.getProjectionRange()
    if (supportRange.length <= 0) {
      return supportRange
    }

    const rawStart = this.sweepStart ?? supportRange.start
    const rawEnd = this.sweepEnd ?? supportRange.end
    const start = Math.max(supportRange.start, Math.min(rawStart, rawEnd))
    const end = Math.min(supportRange.end, Math.max(rawStart, rawEnd))
    if (end <= start) {
      return supportRange
    }
    return { start, end, length: end - start }
  }

  generateLUT(): void {
    const range = this.getSweepProjectionRange()
    if (range.length <= 0) {
      this._lutWriter.fill(this._lutData, 1, 1, 1, 1)
    } else {
      this._lutWriter.write(this._lutData, {
        length: range.length,
        effectTime: this.effectTime,
        duration: this.duration,
        interval: this.interval,
        reverse: false,
        visibleStart: this.visibleStart,
        visibleEnd: this.visibleEnd,
        invertTint: this.invertTint,
        baseTint: this.baseTint,
        edgeTint: this.edgeTint,
        tint: this.tint,
      })
    }

    if (this._lutTexture) {
      this._lutTexture.needsUpdate = true
    }
  }

  getCompletionTime(): number {
    if (this.mode === 'scan') {
      return this.duration
    }
    return (
      this.delay +
      solveWaveTime(this.getSweepProjectionRange().length, this.speed, this.acceleration)
    )
  }

  isEffectComplete(): boolean {
    return this.mode === 'double-wave-reveal' && this.effectTime >= this.getCompletionTime()
  }

  protected override updateEffect(effectTime: number): void {
    if (this.isEffectComplete()) {
      this.setUniform('uObbBypass', true)
      this.finishWithShadersAttached()
      return
    }

    this.setUniform('uObbBypass', false)
    // double-wave 完全由时间和波参数驱动，shader 不读取 LUT，避免每帧重写并上传纹理。
    if (this.mode === 'scan') {
      this.generateLUT()
    }
    const range = this.getSweepProjectionRange()
    this.setUniform('uObbMode', this.mode === 'scan' ? MODE_SCAN : MODE_DOUBLE_WAVE_REVEAL)
    this.setUniform('uLUT', this._lutTexture)
    this.setUniform('uObbOrigin', writeVector3Array(this.origin, this._originArray))
    this.setUniform('uObbAxisX', writeVector3Array(this.axisX, this._axisXArray))
    this.setUniform('uObbAxisY', writeVector3Array(this.axisY, this._axisYArray))
    this.setUniform('uObbAxisZ', writeVector3Array(this.axisZ, this._axisZArray))
    this.setUniform('uObbMin', writeVector3Array(this.min, this._minArray))
    this.setUniform('uObbMax', writeVector3Array(this.max, this._maxArray))
    this.setUniform('uObbDirection', writeVector3Array(this.direction, this._directionArray))
    this.setUniform('uSweepStart', range.start)
    this.setUniform('uSweepEnd', range.end)
    this.setUniform('uTime', effectTime)
    this.setUniform('uSpeed', this.speed)
    this.setUniform('uAcceleration', this.acceleration)
    this.setUniform('uDelay', this.delay)
    this.setUniform('uDotTint', writeColorArray(this.dotTint, this._dotTintArray))
    this.setUniform('uWaveTint', writeColorArray(this.waveTint, this._waveTintArray))
    this.setUniform('uOscillationIntensity', this.oscillationIntensity)
    this.setUniform('uBandWidth', this.bandWidth)
    this.setUniform('uDotSize', this.dotSize)
    this.setUniform('uMaxRoundSize', this.maxRoundSize)
    this.setUniform('uRevealBlendDistance', this.revealBlendDistance)
    if (this.mode === 'double-wave-reveal') {
      this.setUniform('minPixelSize', this.minPixelSize)
    }
  }

  protected override onDestroy(): void {
    this._lutTexture?.dispose()
    this._lutTexture = null
  }

  private createLUTTexture(): THREE.DataTexture {
    const texture = new THREE.DataTexture(
      this._lutData,
      256,
      1,
      THREE.RGBAFormat,
      THREE.HalfFloatType
    )
    texture.name = 'GSplatObbShaderEffectLUT'
    texture.magFilter = THREE.NearestFilter
    texture.minFilter = THREE.NearestFilter
    texture.generateMipmaps = false
    texture.unpackAlignment = 1
    texture.needsUpdate = true
    return texture
  }
}
